import React, { useContext, useState, useMemo, useCallback } from 'react';
import {
  AttestantQuery_node_TEDLRAttestant,
  AttestantQuery_node_TEDLRAttestant__Associations_Reserver_All,
} from '../../Queries/_QueryTypes/AttestantQuery';
import {
  AllCertifiers,
  AllCertifiers_db_LRAttestratt_All,
} from '../../../FakturaDel/Components/FakturaAttestHandleCertifiersModal/Queries/_QueryTypes/AllCertifiers';
import {
  IObjektVyComp,
  IBusinessCollectionProps,
  IObjektVy,
} from '../../../../../../../Types/ObjektVy';
import {
  mergeBusinessDataFormValues,
  parseText,
} from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { BusinessDataFormContext } from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import EDList from '../../../../../../../BaseComponents/List/EDList';
import EDListItem from '../../../../../../../BaseComponents/List/Components/EDListItem';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import { useIsSmallScreen } from '../../../../../../../Hooks/useFullscreenView';
import { useQuery } from '@apollo/client';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import certifierQuery from '../../../FakturaDel/Components/FakturaAttestHandleCertifiersModal/Queries/AllCertifiersQuery.gql';
import { LoginStateContext } from '../../../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import Reserv from '../Reserv/Reserv';
import { makeStyles } from 'tss-react/mui';
import { ReservContext } from '../ReserverContext/ReserverContext';
import Dialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import {
  FormUpdateAssociatonType,
  getFormUpdateValue,
} from '../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import { AttestantType__Associations_Attestratt_All } from '../../Queries/_QueryTypes/AttestantType';
import ReservListItem from './Components/ReserverListItem';
import shortid from 'shortid';

interface IProps extends IBusinessCollectionProps {
  data?: AttestantQuery_node_TEDLRAttestant;
}

const ReserverComp: IObjektVyComp = {
  caption: '',
  comp: (props: IProps) => <ReserverView {...props} />,
};

const useStyles = makeStyles()(theme => ({
  container: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexFlow: 'row nowrap',
    width: '100%',

    ['&:last-child']: { borderBottom: 'none' },
  },
  item: {
    width: 'calc(100% / 3 - 48px / 3)',
    ['&:last-child']: { width: '48px' },
  },

  containerSmall: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.divider}`,

    ['&:last-child']: { borderBottom: 'none' },
  },

  itemSmall: {
    width: '100%',
    ['&:last-child']: { width: '48px' },
  },

  pointer: { cursor: 'pointer' },
}));

export const ReserverView: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes } = useStyles();

  const dataformContext = useContext(BusinessDataFormContext);
  const loginStateContext = useContext(LoginStateContext);
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const reservContext = useContext(ReservContext);
  const isSmallScreen = useIsSmallScreen();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [certifiers, setCertifiers] = useState<
    AllCertifiers_db_LRAttestratt_All[]
  >([]);

  const [removeStandin, setRemoveStandIn] = useState<string>('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);

  const getMergedData = useCallback(
    (data: AttestantQuery_node_TEDLRAttestant) =>
      mergeBusinessDataFormValues(
        parseText(data),
        parseText(dataformContext?.formState.values ?? {})
      ),

    [dataformContext]
  );

  let data: any = props.data
    ? getMergedData(props.data)
    : getMergedData(dataformContext?.data);

  const isCertifier = useMemo(() => {
    return (
      loginStateContext.sign.toLocaleLowerCase() ===
      data?._Associations?.Anvandare?.SIGNATURE.toLocaleLowerCase()
    );
  }, [loginStateContext, data]);

  useQuery<AllCertifiers>(certifierQuery, {
    variables: {
      dbName: activeDatabase ?? '',
      certifyGroup: ' ',
    },
    onCompleted: data => {
      if (
        data?.db &&
        data.db?.LRAttestratt_All &&
        data.db?.LRAttestratt_All.length !== 0
      ) {
        const certifiersList = data.db?.LRAttestratt_All.filter(
          certifier =>
            certifier._Associations?.Attestant?._Associations?.Anvandare?.SIGNATURE.toLocaleLowerCase() !==
            loginStateContext.sign.toLocaleLowerCase()
        );
        setCertifiers(certifiersList);
      }
    },
  });

  const attestratter: AttestantType__Associations_Attestratt_All[] = useMemo(
    () => data._Associations?.Attestratt,
    [data]
  );

  const deleteClick = useCallback(
    (standIn: AttestantQuery_node_TEDLRAttestant__Associations_Reserver_All) =>
      () => {
        setConfirmDialogOpen(true);
        setRemoveStandIn(standIn.id ?? '');
      },
    []
  );

  const onEditClick = useCallback(
    (standIn: AttestantQuery_node_TEDLRAttestant__Associations_Reserver_All) =>
      () => {
        if (isCertifier) {
          reservContext.setEditStandin(standIn);
          reservContext.setAttestrattID(
            standIn._Associations?.Attestratt?.id ?? null
          );
          reservContext.setDialogOpen(true);
        }
      },
    [isCertifier, reservContext]
  );

  return (
    <>
      <EDList
        style={{
          width: '100%',
        }}
        testId="absence-list"
      >
        {data && (data._Associations?.Reserver?.length ?? 0) > 0 ? (
          data._Associations?.Reserver?.map(
            (
              standIn: AttestantQuery_node_TEDLRAttestant__Associations_Reserver_All,
              index: number
            ) => {
              const key = shortid.generate();
              return isSmallScreen ? (
                <ReservListItem
                  deleteClick={deleteClick(standIn)}
                  isAuto={standIn.AUTOSKAPAD ?? false}
                  isCertifier={isCertifier}
                  isSmallScreen={isSmallScreen}
                  key={key}
                  level={standIn.AUTOSKAPAD_LEVEL ?? 0}
                  onEditClick={onEditClick(standIn)}
                  pointer={classes.pointer}
                  standIn={standIn}
                  index={index}
                />
              ) : (
                <ReservListItem
                  deleteClick={deleteClick(standIn)}
                  isAuto={standIn.AUTOSKAPAD ?? false}
                  isCertifier={isCertifier}
                  isSmallScreen={isSmallScreen}
                  key={key}
                  level={standIn.AUTOSKAPAD_LEVEL ?? 0}
                  onEditClick={onEditClick(standIn)}
                  pointer={classes.pointer}
                  standIn={standIn}
                  index={index}
                />
              );
            }
          )
        ) : (
          <EDListItem>
            <EDTypography>Det finns ingen frånvaro registrerad</EDTypography>
          </EDListItem>
        )}
      </EDList>

      <Dialog
        footerActions={[
          {
            label: 'Avbryt',
            onClick: () => {
              dataformContext?.formStateDispatch({
                type: 'CANCEL',
              });
              setConfirmDialogOpen(false);
              setRemoveStandIn('');
            },
          },
          {
            label: 'Ta bort',
            onClick: () => {
              const newValues: FormUpdateAssociatonType[] = [
                {
                  associationName: 'Reserver',
                  id: removeStandin,
                  operation: 'Delete_Values',
                },
              ];

              dataformContext?.saveForm(
                mergeBusinessDataFormValues(
                  getFormUpdateValue(newValues, dataformContext.data ?? {}),
                  dataformContext.formState.values ?? {}
                ),
                () => {
                  setConfirmDialogOpen(false);
                  setRemoveStandIn('');
                }
              );
            },
          },
        ]}
        title="Bekräfta borttag"
        open={confirmDialogOpen}
      >
        <EDTypography>
          Är du säker på att du vill ta bort denna Frånvaroperiod?
        </EDTypography>
      </Dialog>

      <Reserv
        certifiers={certifiers}
        attestratter={attestratter}
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        editReserve={Boolean(reservContext.editStandin)}
        id={reservContext?.editStandin?.id ?? ''}
      />
    </>
  );
};

export const Reserver: IObjektVy = {
  type: 'ObjektVy',
  id: 'Reserver',
  info: {},
  def: () => ReserverComp,
};
